// Renders either the official @react-oauth/google web component (in a browser)
// or a custom Google-branded button that calls the native Capacitor plugin
// (iOS). Both paths produce a Google id_token and call onSuccess(idToken).
//
// The web component refuses to render inside a WKWebView (Google's policy),
// so on native we draw our own button matching the visual weight of the
// Sign in with Apple button next to it.

import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { isNativePlatform } from '@/services/platform';
import { signInWithGoogleNative } from '@/services/googleSignIn';

interface Props {
  onSuccess: (idToken: string) => void | Promise<void>;
  onError: (message: string) => void;
  mode: 'signin' | 'signup';
  isLoading: boolean;
}

const GoogleGLogo: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export const GoogleAuthButton: React.FC<Props> = ({ onSuccess, onError, mode, isLoading }) => {
  const [nativePending, setNativePending] = useState(false);

  if (isNativePlatform()) {
    const label = mode === 'signup' ? 'Sign up with Google' : 'Continue with Google';
    const disabled = isLoading || nativePending;

    const handleClick = async () => {
      setNativePending(true);
      try {
        const idToken = await signInWithGoogleNative();
        await onSuccess(idToken);
      } catch (err: any) {
        // The native plugin throws on user cancellation. Suppress those silently;
        // surface anything else as a generic error to the parent. Always log the
        // real error to the console so iOS debugging via Xcode is possible.
        console.error('[GoogleAuth] Native sign-in error:', err);
        const msg = (err?.message || '').toLowerCase();
        const cancelled = msg.includes('cancel') || msg.includes('canceled') || msg.includes('12501');
        if (!cancelled) {
          onError('Google sign-in failed. Please try again.');
        }
      } finally {
        setNativePending(false);
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={label}
        className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium text-base bg-white text-gray-700 hover:bg-gray-100 transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <GoogleGLogo />
        <span>{nativePending ? 'Loading...' : label}</span>
      </button>
    );
  }

  // Web: official Google component
  return (
    <div className={`flex justify-center ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
      <GoogleLogin
        onSuccess={(cr) => cr.credential && onSuccess(cr.credential)}
        onError={() => onError('Google sign-in failed. Please try again.')}
        theme="filled_blue"
        shape="rectangular"
        text={mode === 'signup' ? 'signup_with' : 'continue_with'}
        width="320"
      />
    </div>
  );
};

export default GoogleAuthButton;
